import { ProductListItem } from '@/types';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

export const PAGE_TITLE_CLASSES =
    'text-2xl md:text-3xl font-bold text-brand-mist uppercase leading-tight tracking-tight';

export function parsePrice(price: string | number): number {
    if (typeof price === 'number') return price;
    if (!price) return 0;
    return parseInt(price.replace(/\D/g, ''), 10) || 0;
}

type QueryRecord = Record<string, string>;

export class QueryParams {
    private readonly sp: globalThis.URLSearchParams;

    // Опциональный кэш (полезно, если ты много раз читаешь params одного и того же url)
    private static cache = new Map<string, QueryParams>();

    constructor(url: string, base: string = window.location.origin) {
        // URL() корректно собирает абсолютный URL из относительного + base
        this.sp = new URL(url, base).searchParams;
    }

    static from(
        url: string,
        opts?: { base?: string; cache?: boolean },
    ): QueryParams {
        const base = opts?.base ?? window.location.origin;
        const useCache = opts?.cache ?? true;

        const key = `${base}||${url}`;
        if (useCache) {
            const hit = this.cache.get(key);
            if (hit) return hit;
        }

        const inst = new QueryParams(url, base);
        if (useCache) this.cache.set(key, inst);
        return inst;
    }

    /** Все параметры (последнее значение победит при дубликатах ключей). */
    all(): QueryRecord {
        // entries() отдаёт пары [key, value], удобно для Object.fromEntries [web:35]
        return Object.fromEntries(this.sp.entries());
    }

    get(name: string): string | null {
        return this.sp.get(name);
    }

    has(name: string): boolean {
        return this.sp.has(name);
    }

    /** Для кейсов вида ?tag=a&tag=b */
    getAll(name: string): string[] {
        return this.sp.getAll(name);
    }

    getInt(name: string): number | null {
        const v = this.get(name);
        if (v == null) return null;
        const n = Number.parseInt(v, 10);
        return Number.isNaN(n) ? null : n;
    }

    getBool(name: string): boolean | null {
        const v = this.get(name);
        if (v == null) return null;
        return v === '1' || v === 'true' || v === 'yes' || v === 'on';
    }
}

export const cleanedPhone = (s: string): string =>
    s.replace(/[^\d+]/g, '').replace(/(?!^)\+/g, '');

export const handleIsMobile = (): boolean => {
    // Проверка User Agent
    const userAgent = navigator.userAgent;
    const mobileRegex =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i;

    if (mobileRegex.test(userAgent)) {
        return true;
    }

    // Дополнительная проверка по ширине экрана
    return window.innerWidth < 768;
};

export const kegPrice = (variation: ProductListItem) => {
    const isKeg =
        variation?.packaging_type?.toLowerCase()?.includes('кег') ?? false;
    const numericPrice = parsePrice(variation.price);
    const numericVolume = parseFloat(variation.volume);
    const pricePerLiter =
        isKeg && numericVolume > 0
            ? Math.round(numericPrice / numericVolume)
            : null;

    return { isKeg, pricePerLiter };
};
